import { Header } from "@/components/Header";
import { useSEO } from "@/hooks/use-seo";
import { Footer } from "@/components/Footer";
import { Users, Award, BookOpen, Shield, TrendingUp, Star, CheckCircle, ExternalLink, Calendar } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import mykhailo from "@assets/image_1773345523383.png";
import rebecca from "@assets/image_1773345527765.png";

const AUTHORS = [
  {
    name: "Mykhailo Romaenko",
    role: "Hlavní editor & zakladatel",
    photo: mykhailo,
    experience: "12+ let v oboru",
    tags: ["Online kasina", "Bonusové strategie", "Zodpovědné hraní", "Regulace trhu"],
    bio: "Mykhailo se online hazardu věnuje přes dvanáct let. Začínal jako hráč, postupně se stal analytikem kasín a nakonec zakladatelem editorského týmu hrajbonus.com. Je odborníkem na českou legislativu online hazardu, srovnávání bonusových nabídek a hodnocení bezpečnosti platforem. Každá recenze, která vychází pod jeho jménem, prošla osobním testováním od registrace až po výběr výher.",
    expertise: [
      "Osobní testování každého kasina před publikací",
      "Hloubková analýza bonusových podmínek (RTP, wagering)",
      "Sledování legislativních změn na českém trhu",
      "Koordinace redakčního týmu a standardů kvality",
    ],
    linkedin: "https://www.linkedin.com/in/mykhailo-romaenko-hrajbonus",
  },
  {
    name: "Rebeca Jimenez",
    role: "Senior Editorial Manager & recenzentka",
    photo: rebecca,
    experience: "8+ let v oboru",
    tags: ["Bonusové podmínky", "Platební metody", "Fact-checking", "iGaming"],
    bio: "Rebeca je editorka a recenzentka s více než osmi lety zkušeností v oblasti online kasin a iGaming médií. Specializuje se na ověřování bonusových podmínek, testování platebních metod a hodnocení uživatelského prostředí herních platforem. Na hrajbonus.com zajišťuje, aby všechny informace byly přesné, aktuální a odpovídaly aktuálním regulacím i trendům na trhu.",
    expertise: [
      "Ověřování bonusových podmínek a wagering requirements",
      "Testování platebních metod a rychlosti výběrů",
      "Fact-checking a aktualizace recenzního obsahu",
      "Hodnocení uživatelského prostředí herních platforem",
    ],
    linkedin: "https://www.linkedin.com/in/rebeca-jimenez-hrajbonus",
  },
];

const STANDARDS = [
  { icon: CheckCircle, title: "Osobní testování", desc: "Každé kasino musí projít reálným testováním od registrace po výběr. Žádná recenze není napsána bez přímé zkušenosti." },
  { icon: Shield, title: "Nezávislost", desc: "Affiliate spolupráce nikdy neovlivňuje hodnocení. Kasina jsou hodnocena podle stejných kritérií bez ohledu na výši provize." },
  { icon: BookOpen, title: "Průběžná aktualizace", desc: "Recenze jsou pravidelně revidovány — při změně bonusů, licencí nebo platebních podmínek je obsah okamžitě upraven." },
  { icon: Star, title: "Transparentnost", desc: "Každá recenze je podepsaná konkrétním autorem. Vždy víte, kdo a na základě čeho hodnocení vypracoval." },
];

export default function NasiAutoriPage() {
  useSEO({ title: "Naši Autoři – Experti na kasina a hazardní hry | HrajBonus", description: "Seznamte se s autory recenzí HrajBonus. Zkušení odborníci s lety praxe v oblasti online kasin, bonusů a hazardních her v ČR. 18+",
  url: "https://hrajbonus.com/nasi-autori", });

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f8fc]">
      <Header />

      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 25% 50%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 sm:py-20 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-semibold uppercase tracking-widest">Redakce</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5 max-w-2xl">
            Naši autoři
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl">
            Každá recenze na hrajbonus.com je podepsaná konkrétním odborníkem z naší redakce. Poznáte je, jejich zkušenosti i jejich přístup k testování. Žádná anonymní doporučení, žádný neověřený obsah.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5" data-testid="badge-updated-authors">
            <Calendar className="w-3.5 h-3.5 text-white/70" />
            <span className="text-white/80 text-xs font-semibold">Aktualizováno: 12. března 2026</span>
          </div>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-20 flex-1 space-y-16">

        <section>
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <Award className="w-3.5 h-3.5" /> Redakční tým
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-3">Lidé za obsahem hrajbonus.com</h2>
            <p className="text-gray-600 max-w-2xl leading-relaxed">
              Náš tým tvoří odborníci s dohromady více než 20 lety zkušeností v oblasti online hazardu, finančních technologií a digitálního publishingu.
            </p>
          </div>

          <div className="space-y-8">
            {AUTHORS.map((author) => (
              <div key={author.name} className="bg-white rounded-3xl border border-[#dde4ee] shadow-sm overflow-hidden" data-testid={`card-author-${author.name.split(" ")[0].toLowerCase()}`}>
                <div className="grid lg:grid-cols-3">
                  <div className="p-8 flex flex-col items-center text-center lg:border-r border-[#dde4ee]" style={{ background: "linear-gradient(135deg, #f3f6fb, #e8eef8)" }}>
                    <div className="relative mb-5" data-testid={`avatar-author-${author.name.split(" ")[0].toLowerCase()}`}>
                      <div className="w-28 h-28 rounded-full overflow-hidden shadow-lg ring-4 ring-white">
                        <img
                          src={author.photo}
                          alt={author.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-1">{author.name}</h3>
                    <p className="text-[#003da5] text-sm font-semibold mb-2">{author.role}</p>
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-3">
                      <TrendingUp className="w-3.5 h-3.5" />
                      {author.experience}
                    </div>
                    {author.linkedin && (
                      <a
                        href={author.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#0a66c2] text-xs font-semibold hover:underline mb-4 transition-colors"
                        data-testid={`link-linkedin-${author.name.split(" ")[0].toLowerCase()}`}
                      >
                        <SiLinkedin className="w-4 h-4" />
                        LinkedIn profil
                      </a>
                    )}
                    <div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Témata</span>
                      <div className="flex flex-wrap justify-center gap-1.5">
                        {author.tags.map((tag) => (
                          <span key={tag} className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#003da5]/10 text-[#003da5] border border-[#003da5]/20">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2 p-8">
                    <p className="text-gray-600 leading-relaxed mb-6">{author.bio}</p>
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Oblast odbornosti</h4>
                      <ul className="space-y-2">
                        {author.expertise.map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <Shield className="w-3.5 h-3.5" /> Redakční standardy
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-3">Naše zásady tvorby obsahu</h2>
            <p className="text-gray-600 max-w-2xl leading-relaxed">
              Všichni autoři se řídí stejnými redakčními standardy, které zajišťují konzistentní, férový a transparentní obsah.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {STANDARDS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <a
              href="/redakcni-metodologie"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4"
              data-testid="cta-methodology"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Naše metodologie</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Zjistěte, jak vzniká každá recenze kasina krok za krokem.</p>
              </div>
            </a>
            <a
              href="/o-nas"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4"
              data-testid="cta-about"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">O nás</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Poznejte naši misi, hodnoty a proč se zaměřujeme na český trh.</p>
              </div>
            </a>
            <a
              href="/kontakt"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4 sm:col-span-2 lg:col-span-1"
              data-testid="cta-contact"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <ExternalLink className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Spolupráce</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Chcete s námi spolupracovat? Napište nám.</p>
              </div>
            </a>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
